'use client';

import { debounce } from '@/utils/debounce';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { SearchField } from '@andritzot/metris-web-ui/inputs/search-field';
import { ToggleButton, ToggleButtonGroup } from '@andritzot/metris-web-ui/inputs/toggle-button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { AutoFixHighOutlined, DashboardOutlined, ViewModuleOutlined } from '@mui/icons-material';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { useCallback, useMemo, useState } from 'react';

import { useHelpWizardStore } from '../../store/helpWizard';
import { useSolutionStore } from '../../store/solution';
import { HelpDialog } from './HelpWizard';

const layoutOptions = [
  { icon: <DashboardOutlined />, value: 'card' },
  { icon: <ViewModuleOutlined />, value: 'grid' },
];

interface HeaderProps {
  /** Rendered as the page's only <h1>; each listing page names its own scope (e.g. "Pulp Solutions"). */
  title: string;
}

const Header = ({ title }: HeaderProps) => {
  const [searchText, setSearchText] = useState('');
  const isHelpDialogOpen = useHelpWizardStore((s) => s.isDialogOpen);

  const layout = useSolutionStore((s) => s.layout);
  const { SiteSearch } = usePiwikPro();

  const handleOpenHelpDialog = () => useHelpWizardStore.setState({ isDialogOpen: true });
  const handleCloseHelpDialog = () => useHelpWizardStore.setState({ isDialogOpen: false });

  const debouncedSearchActions = useMemo(
    () =>
      debounce((value: string) => {
        SiteSearch.trackSiteSearch(value);
        useSolutionStore.setState({ searchValue: value });
      }, 300),
    [SiteSearch],
  );

  const handleSearchText = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
      debouncedSearchActions(e.target.value);
    },
    [debouncedSearchActions],
  );

  const handleLayoutOptionChange = (_: React.MouseEvent, value: 'card' | 'grid' | null) =>
    useSolutionStore.setState({ layout: value ?? undefined });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: 2, md: 4 },
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        p: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 } }}>
        <Typography variant="h5" component="h1" color="primary.dark" sx={{ p: 2, flexShrink: 0 }}>
          {title}
        </Typography>
        <SearchField
          data-testid="search-input"
          value={searchText}
          onChange={handleSearchText}
          placeholder="Search"
          sx={{ width: '100%', backgroundColor: 'white' }}
          size="small"
          fullWidth
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AutoFixHighOutlined />}
          onClick={handleOpenHelpDialog}
          sx={{ flex: { xs: 1, md: 'initial' } }}
        >
          Find My Solution
        </Button>

        <ToggleButtonGroup exclusive value={layout} onChange={handleLayoutOptionChange}>
          {layoutOptions.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              sx={{
                '&.Mui-selected, &.Mui-selected:focus': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                },
                '&.Mui-selected:hover': { backgroundColor: 'primary.dark', color: 'primary.contrastText' },
              }}
            >
              {option.icon}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {isHelpDialogOpen && <HelpDialog handleClose={handleCloseHelpDialog} />}
    </Box>
  );
};

export default Header;
